import React, { useState } from 'react';
import { Upload, Trash2, FileText, Image, File, Download, ChevronDown, ChevronUp, Plus, X, Globe, Copy, Check, Link, ExternalLink, Music } from 'lucide-react';
import DragDropZone from '../DragDropZone';

export default function PressKitSection({
  bandId,
  bandDocs, songs, demoSongs, loading, isMobile,
  publicPressKitEnabled, publicPressKitId,
  onTogglePublicPressKit, onBandDocUpload, onDeleteBandDoc, onDeleteSingleDoc,
  onUpdateBandContact, onAddDemoSong, onRemoveDemoSong, setError
}) {
  const [demoSongsExpanded, setDemoSongsExpanded] = useState(false);
  const [photoAndBiosExpanded, setPhotoAndBiosExpanded] = useState(false);
  const [livePhotosExpanded, setLivePhotosExpanded] = useState(false);
  const [technicalDocsExpanded, setTechnicalDocsExpanded] = useState(false);
  const [contactsExpanded, setContactsExpanded] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [togglingPressKit, setTogglingPressKit] = useState(false);

  // Song picker
  const [showSongPicker, setShowSongPicker] = useState(false);
  const [selectedSongForVersion, setSelectedSongForVersion] = useState(null);

  // Helpers
  const getSongById = (songId) => songs.find(s => s.id === songId);
  const getVersionById = (song, versionId) => {
    if (!song?.versions || !versionId) return null;
    return song.versions[versionId] || null;
  };
  const getSortedVersions = (song) => {
    if (!song?.versions) return [];
    return Object.entries(song.versions)
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
  };
  const availableSongs = songs.filter(song => {
    const hasAudio = song.versions && Object.keys(song.versions).length > 0;
    return hasAudio && !demoSongs.some(d => d.songId === song.id);
  });

  const getPressKitLink = () => `https://tapsody.app/presskit/${publicPressKitId}`;
  const copyPressKitLink = () => {
    navigator.clipboard.writeText(getPressKitLink());
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleTogglePressKit = async () => {
    setTogglingPressKit(true);
    await onTogglePublicPressKit();
    setTogglingPressKit(false);
  };

  // Shared input class
  const inputCls = "w-full bg-transparent border border-surface-border-light px-4 py-2 text-white placeholder-text-subtle focus:outline-none focus:border-brand text-sm rounded-pill";

  // Reusable doc row
  const DocRow = ({ doc, type, label }) => (
    <div className="border border-surface-border-light rounded-card-btn p-3 flex items-center justify-between">
      <div>
        <p className="text-sm font-semibold">{doc.fileName}</p>
        <p className="text-xs text-text-subtle">
          {new Date(doc.uploadDate).toLocaleDateString()}
          {doc.uploadedByName && ` • Uploaded by ${doc.uploadedByName}`}
        </p>
      </div>
      <div className="flex gap-1">
        <a href={doc.fileURL} target="_blank" rel="noopener noreferrer" className="bg-transparent hover:bg-brand transition p-2 rounded-full">
          <File className="w-4 h-4 text-white" />
        </a>
        <a href={doc.fileURL} download={doc.fileName} className="bg-transparent hover:bg-brand transition p-2 rounded-full">
          <Download className="w-4 h-4 text-white" />
        </a>
        <button onClick={() => { if (window.confirm(`Delete this ${label}?`)) onDeleteBandDoc(type, doc.id, doc.storagePath); }} className="bg-transparent hover:bg-red-600 transition p-2 rounded-full">
          <Trash2 className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );

  // Reusable single-file doc (bio, band photo)
  const SingleDocRow = ({ doc, type, label }) => (
    <div className="border border-surface-border-light rounded-card-btn p-3 flex items-center justify-between">
      <div>
        <p className="text-sm font-semibold">{doc.fileName}</p>
        <p className="text-xs text-text-subtle">
          {new Date(doc.uploadDate).toLocaleDateString()}
          {doc.uploadedByName && ` • Uploaded by ${doc.uploadedByName}`}
        </p>
      </div>
      <div className="flex gap-1">
        <a href={doc.fileURL} target="_blank" rel="noopener noreferrer" className="bg-transparent hover:bg-brand transition p-2 rounded-full">
          <File className="w-4 h-4 text-white" />
        </a>
        <a href={doc.fileURL} download={doc.fileName} className="bg-transparent hover:bg-brand transition p-2 rounded-full">
          <Download className="w-4 h-4 text-white" />
        </a>
        <button onClick={() => { if (window.confirm(`Delete ${label}?`)) onDeleteSingleDoc(type, doc.storagePath); }} className="bg-transparent hover:bg-red-600 transition p-2 rounded-full">
          <Trash2 className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );

  // Collapsible section wrapper
  const Section = ({ icon: Icon, title, badge, expanded, onToggle, children }) => (
    <div className="card-3d rounded-card overflow-hidden">
      <button onClick={onToggle} className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-white/5 transition text-left">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Icon className="w-5 h-5 text-white" />
          {title}
          {badge !== undefined && (
            <span className="text-sm font-normal text-text-subtle">({badge})</span>
          )}
        </h2>
        {expanded ? <ChevronUp className="w-5 h-5 text-text-subtle" /> : <ChevronDown className="w-5 h-5 text-text-subtle" />}
      </button>
      {expanded && <div className="border-t border-surface-border-light p-4 sm:p-6">{children}</div>}
    </div>
  );

  return (
    <>
      {/* Press Kit Header */}
      <div className="border-t border-surface-border-light pt-6">
        <h2 className="text-lg font-semibold text-text-subtle mb-2">Press Kit</h2>
        <p className="text-sm text-text-subtle mb-4">Professional materials for venues and promoters.</p>
      </div>

      {/* Demo Songs */}
      <Section icon={Music} title="Demo Songs" badge={`${demoSongs.length}/3`} expanded={demoSongsExpanded} onToggle={() => setDemoSongsExpanded(!demoSongsExpanded)}>
        <p className="text-sm text-text-subtle mb-4">
          Select up to 3 songs to showcase on your public press kit. Only songs with uploaded audio will appear.
        </p>

        {demoSongs.length > 0 ? (
          <div className="space-y-2 mb-4">
            {demoSongs.map((demoItem, index) => {
              const song = getSongById(demoItem.songId);
              const version = song ? getVersionById(song, demoItem.versionId) : null;
              return (
                <div key={demoItem.songId} className="border border-surface-border-light rounded-card-btn p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-brand font-bold w-6">{index + 1}</span>
                    <div>
                      <p className="font-semibold">{song?.name || 'Unknown Song'}</p>
                      {version && (
                        <p className="text-xs text-text-subtle">
                          {version.versionName || 'Version'} • {new Date(version.uploadDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <button onClick={() => onRemoveDemoSong(demoItem.songId)} className="bg-transparent hover:bg-red-600 transition p-2 rounded-full">
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border border-dashed border-surface-border-light rounded-card-btn p-6 text-center mb-4">
            <Music className="w-8 h-8 text-text-subtle opacity-50 mx-auto mb-2" />
            <p className="text-text-muted text-sm">No demo songs selected yet</p>
          </div>
        )}

        {demoSongs.length < 3 && (
          <button onClick={() => setShowSongPicker(true)} className="bg-brand hover:bg-brand-hover transition px-4 py-2 font-semibold text-sm flex items-center gap-2 rounded-pill">
            <Plus className="w-4 h-4" /> Add Demo Song
          </button>
        )}
      </Section>

      {/* Song Picker Modal */}
      {showSongPicker && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="card-3d border border-surface-border-light rounded-card w-full max-w-md max-h-[70vh] flex flex-col">
            <div className="p-4 border-b border-surface-border-light flex items-center justify-between">
              <h3 className="text-lg font-semibold">{selectedSongForVersion ? 'Select Version' : 'Select Demo Song'}</h3>
              <button onClick={() => { setShowSongPicker(false); setSelectedSongForVersion(null); }} className="text-text-subtle hover:text-white transition">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {selectedSongForVersion ? (
                <>
                  <button onClick={() => setSelectedSongForVersion(null)} className="text-sm text-brand hover:underline mb-3 flex items-center gap-1">← Back to songs</button>
                  <p className="text-sm text-text-subtle mb-3">
                    Selecting version for: <span className="text-white font-semibold">{selectedSongForVersion.name}</span>
                  </p>
                  <div className="space-y-2">
                    {getSortedVersions(selectedSongForVersion).map((version, idx, arr) => (
                      <button
                        key={version.id}
                        onClick={() => { onAddDemoSong(selectedSongForVersion.id, version.id); setShowSongPicker(false); setSelectedSongForVersion(null); }}
                        className="w-full border border-surface-border-light hover:bg-white/10 transition rounded-card-btn p-3 text-left"
                      >
                        <p className="font-semibold">Version {arr.length - idx}</p>
                        <p className="text-xs text-text-subtle">{new Date(version.uploadDate).toLocaleDateString()} • {version.fileName || 'audio file'}</p>
                      </button>
                    ))}
                  </div>
                </>
              ) : availableSongs.length > 0 ? (
                <div className="space-y-2">
                  {availableSongs.map(song => (
                    <button
                      key={song.id}
                      onClick={() => setSelectedSongForVersion(song)}
                      className="w-full border border-surface-border-light hover:bg-white/10 transition rounded-card-btn p-3 text-left flex items-center gap-3"
                    >
                      <Music className="w-5 h-5 text-brand" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{song.name}</p>
                        <p className="text-xs text-text-subtle">{getSortedVersions(song).length} version{getSortedVersions(song).length !== 1 ? 's' : ''}</p>
                      </div>
                      <ChevronDown className="w-5 h-5 text-text-subtle -rotate-90" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Music className="w-12 h-12 text-text-subtle opacity-50 mx-auto mb-3" />
                  <p className="text-text-muted">No songs with audio available</p>
                  <p className="text-text-subtle text-sm mt-1">Upload audio to your songs first</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Press Kit Sharing */}
      <div className="card-3d rounded-card p-4 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="bg-brand/20 p-3 rounded-full flex-shrink-0">
            <Globe className="w-6 h-6 text-brand" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-1">Public Press Kit Page</h3>
            <p className="text-sm text-text-subtle mb-4">
              Share your press kit with venues via a public link. They can view and download all your materials.
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                onClick={handleTogglePressKit}
                disabled={togglingPressKit}
                className={`px-4 py-2 font-semibold text-sm flex items-center gap-2 transition rounded-pill ${
                  publicPressKitEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-white/10 hover:bg-white/20'
                }`}
              >
                {togglingPressKit ? 'Updating...' : publicPressKitEnabled ? (<><Check className="w-4 h-4" />Enabled</>) : (<><Globe className="w-4 h-4" />Enable Public Page</>)}
              </button>

              {publicPressKitEnabled && publicPressKitId && (
                <>
                  <button onClick={copyPressKitLink} className="bg-brand hover:bg-brand-hover transition px-4 py-2 font-semibold text-sm flex items-center gap-2 rounded-pill">
                    {copiedLink ? (<><Check className="w-4 h-4" />Copied!</>) : (<><Copy className="w-4 h-4" />Copy Link</>)}
                  </button>
                  <a href={getPressKitLink()} target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-white/20 transition px-4 py-2 font-semibold text-sm flex items-center gap-2 rounded-pill">
                    <ExternalLink className="w-4 h-4" />Preview
                  </a>
                </>
              )}
            </div>

            {publicPressKitEnabled && publicPressKitId && (
              <p className="text-xs text-text-subtle mt-3 flex items-center gap-1">
                <Link className="w-3 h-3" />{getPressKitLink()}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Band Photo & Bios */}
      <Section icon={Image} title="Band Photo & Bios" expanded={photoAndBiosExpanded} onToggle={() => setPhotoAndBiosExpanded(!photoAndBiosExpanded)}>
        <div className="space-y-6">
          {/* Band Photo */}
          <div>
            <h3 className="font-semibold mb-3">Band Photo</h3>
            {bandDocs.bandPhoto ? (
              <div className="border border-surface-border-light rounded-card-btn p-3">
                <img src={bandDocs.bandPhoto.fileURL} alt="Band" className="w-full max-w-md rounded-card-btn mb-2" />
                <p className="text-xs text-text-subtle mb-2">
                  Uploaded {new Date(bandDocs.bandPhoto.uploadDate).toLocaleDateString()}
                  {bandDocs.bandPhoto.uploadedByName && ` by ${bandDocs.bandPhoto.uploadedByName}`}
                </p>
                <div className="flex gap-1 mt-2">
                  <a href={bandDocs.bandPhoto.fileURL} target="_blank" rel="noopener noreferrer" className="bg-brand hover:bg-brand-hover transition px-4 py-2 text-sm font-semibold rounded-pill">View Full Size</a>
                  <a href={bandDocs.bandPhoto.fileURL} download={bandDocs.bandPhoto.fileName} className="bg-transparent hover:bg-brand transition p-2 rounded-full">
                    <Download className="w-4 h-4 text-white" />
                  </a>
                  <button onClick={() => { if (window.confirm('Delete band photo?')) onDeleteSingleDoc('bandPhoto', bandDocs.bandPhoto.storagePath); }} className="bg-transparent hover:bg-red-600 transition p-2 rounded-full">
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            ) : (
              <DragDropZone accept=".jpg,.jpeg,.png" onFileSelect={(file) => onBandDocUpload('bandPhoto', file)} disabled={loading} icon={Image} message="Drag and drop band photo here (.jpg, .png)" isMobile={isMobile} />
            )}
          </div>

          {/* Bios */}
          <div>
            <h3 className="font-semibold mb-3">Band Biography</h3>
            <div className="space-y-3">
              {[
                { key: 'bioSmall', label: 'Small (50-100 words)' },
                { key: 'bioMedium', label: 'Medium (150-300 words)' },
                { key: 'bioLarge', label: 'Large (500+ words)' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <p className="text-sm text-text-subtle mb-2">{label}</p>
                  {bandDocs[key] ? (
                    <SingleDocRow doc={bandDocs[key]} type={key} label={label.toLowerCase()} />
                  ) : (
                    <DragDropZone accept=".doc,.docx,.pdf,.txt" onFileSelect={(file) => onBandDocUpload(key, file)} disabled={loading} icon={FileText} message={`Upload ${label.split(' ')[0].toLowerCase()} bio`} isMobile={isMobile} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Live Photos */}
      <Section icon={Image} title="Live Photos" badge={bandDocs.livePhotos.length > 0 ? bandDocs.livePhotos.length : undefined} expanded={livePhotosExpanded} onToggle={() => setLivePhotosExpanded(!livePhotosExpanded)}>
        <DragDropZone accept=".jpg,.jpeg,.png" onFileSelect={(file) => onBandDocUpload('livePhotos', file)} disabled={loading} icon={Upload} message="Drag and drop live photos here (.jpg, .png)" isMobile={isMobile} className="mb-3" />
        {bandDocs.livePhotos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {bandDocs.livePhotos.map(photo => (
              <div key={photo.id} className="relative group">
                <img src={photo.fileURL} alt="Live performance" className="w-full h-40 object-cover rounded-card-btn" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1 rounded-b-card-btn">
                  <p className="text-xs text-white truncate">{photo.uploadedByName && `By ${photo.uploadedByName}`}</p>
                </div>
                <button onClick={() => { if (window.confirm('Delete this live photo?')) onDeleteBandDoc('livePhotos', photo.id, photo.storagePath); }} className="absolute top-2 right-2 bg-transparent hover:bg-red-600 transition p-2 opacity-0 group-hover:opacity-100 rounded-full">
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Technical Documents */}
      <Section icon={FileText} title="Technical Documents" expanded={technicalDocsExpanded} onToggle={() => setTechnicalDocsExpanded(!technicalDocsExpanded)}>
        <div className="space-y-6">
          {[
            { key: 'rider', label: 'Band Rider', accept: '.doc,.docx,.pdf' },
            { key: 'gearList', label: 'Gear List', accept: '.doc,.docx,.pdf' },
          ].map(({ key, label, accept }) => (
            <div key={key}>
              <h3 className="font-semibold mb-3">{label}</h3>
              <DragDropZone accept={accept} onFileSelect={(file) => onBandDocUpload(key, file)} disabled={loading} icon={Upload} message={`Drag and drop ${label.toLowerCase()} here (${accept})`} isMobile={isMobile} className="mb-3" />
              {bandDocs[key].length > 0 && (
                <div className="space-y-2">
                  {bandDocs[key].map(doc => <DocRow key={doc.id} doc={doc} type={key} label={label.toLowerCase()} />)}
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* Contacts */}
      <Section icon={FileText} title="Contacts" expanded={contactsExpanded} onToggle={() => setContactsExpanded(!contactsExpanded)}>
        <div className="space-y-6">
          {[
            { key: 'musicLabel', label: 'Music Label', namePlaceholder: 'Label name...', contactPlaceholder: 'Contact info (email, phone, etc.)...' },
            { key: 'booker', label: 'Booker', namePlaceholder: 'Booker name...', contactPlaceholder: 'Contact info (email, phone, etc.)...' },
          ].map(({ key, label, namePlaceholder, contactPlaceholder }) => (
            <div key={key}>
              <h3 className="font-semibold mb-3">{label}</h3>
              <div className="space-y-2">
                <input type="text" value={bandDocs[key].name || ''} onChange={(e) => onUpdateBandContact(key, 'name', e.target.value)} placeholder={namePlaceholder} className={inputCls} />
                <input type="text" value={bandDocs[key].contact || ''} onChange={(e) => onUpdateBandContact(key, 'contact', e.target.value)} placeholder={contactPlaceholder} className={inputCls} />
              </div>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}
